import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Lock } from 'lucide-react';

const ProductFormPricing = ({
  formData,
  setFormData,
  onInwardPriceChange,
  onGstChange,
  onOutwardChange,
  autoStatus,
  outwardLocked,
  isNewProduct,
}) => {
  const gstSlabs = ['0', '5', '12', '18', '28', '40'];

  // Outward price input handler — falls back to local setFormData when the
  // new auto-pricing callbacks aren't wired (so the component stays
  // backwards-compatible with any other caller).
  const handleOutwardChange = (e) => {
    const v = e.target.value;
    if (typeof onOutwardChange === 'function') {
      onOutwardChange(v);
    } else {
      setFormData({ ...formData, outward_selling_price: v });
    }
  };

  const showAutoBadge = autoStatus && autoStatus.applied;
  const showLockBadge = outwardLocked;

  return (
    <>
      <div className="space-y-2">
        <Label>MRP *</Label>
        <Input
          type="number"
          step="0.01"
          value={formData.mrp}
          onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
          required
          data-testid="product-mrp-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Inward Price (Incl. GST) *</Label>
        <Input
          type="number"
          step="0.01"
          value={formData.inward_price_incl_gst}
          onChange={(e) => onInwardPriceChange(e.target.value)}
          required
          data-testid="product-inward-incl-gst-input"
        />
        <p className="text-xs text-gray-500">GST-inclusive purchase price</p>
      </div>

      <div className="space-y-2">
        <Label>Inward Price (Excl. GST)</Label>
        <Input type="number" value={formData.inward_price_excl_gst} readOnly className="bg-gray-50" />
        <p className="text-xs text-blue-600">Auto-calculated</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Outward Selling Price *</Label>
          {showAutoBadge && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200"
              data-testid="product-outward-auto-badge"
              title={`Auto-filled from Margin Master · Our Margin = ${autoStatus.our_margin_pct}%`}
            >
              <Sparkles size={11} /> Auto · {autoStatus.our_margin_pct}%
            </span>
          )}
          {showLockBadge && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
              data-testid="product-outward-locked-badge"
              title="Manual override — clear the field to resume auto-pricing"
            >
              <Lock size={11} /> Manual
            </span>
          )}
        </div>
        <Input
          type="number"
          step="0.01"
          value={formData.outward_selling_price}
          onChange={handleOutwardChange}
          required
          data-testid="product-outward-selling-price-input"
        />
        {autoStatus && !autoStatus.applied && autoStatus.message && (
          <p className="text-xs text-amber-600" data-testid="product-outward-auto-warning">
            {autoStatus.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>HSN Code *</Label>
        <Input value={formData.hsn_code} onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })} required />
      </div>

      <div className="space-y-2">
        <Label>GST % *</Label>
        <Select value={formData.gst_percentage} onValueChange={onGstChange}>
          <SelectTrigger data-testid="product-gst-percentage-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {gstSlabs.map((s) => <SelectItem key={s} value={s}>{s}%</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );
};

export default ProductFormPricing;
